import {
  dashboardPath,
  loginPath,
  onboardPath,
  VITE_API_URL,
  VITE_BASE_URL,
} from "@/constants";
import type { OrgMin, VerifyRespType } from "@/lib/types";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useOrg } from "./organization";

interface Org {
  id: string;
  name: string;
  slug: string;
}

interface Account {
  id: string;
  email: string;
  orgs: Org[];
}

interface Context {
  user: {
    account: Account | undefined;
  };
  handleUser: (data: OrgMin) => void;
  replaceOrg: (removedOrgId: string, org: OrgMin) => void;
  authorize: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => string | undefined;
  setToken: (token: string) => void;
  isLoading: boolean;
  getOrg: (orgId: string) => Org | undefined;
  handleTokenInvalid: () => Promise<void>;
}

const AuthContext = createContext<Context>({} as Context);

function AuthProvider({ children }: { children: ReactNode }) {
  const { storeOrg, removeOrg } = useOrg();
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<{ account: Account | undefined }>(() => {
    const account = window.localStorage.getItem("account");
    return {
      account:
        account !== "undefined"
          ? (JSON.parse(account as string) as Account)
          : undefined,
    };
  });

  useEffect(() => {
    setIsLoading(true);
    const account = window.localStorage.getItem("account");
    if (account !== "undefined") {
      const parsed = JSON.parse(account as string) as Account;

      setUser({
        account: parsed,
      });
    }
    setIsLoading(false);
  }, []);

  const handleUser = (data: OrgMin) => {
    if (!user.account) {
      return;
    }

    const orgs = user.account.orgs;
    const updatedUser = {
      ...user.account,
      orgs: !orgs
        ? [{ id: data.id, name: data.name, slug: data.slug }]
        : user.account.orgs.map((org) =>
            org.id === data.id
              ? { ...org, name: data.name, slug: data.slug }
              : org,
          ),
    };

    // this is if we're switching orgs
    storeOrg(data.id);
    window.localStorage.setItem("account", JSON.stringify(updatedUser));
    setUser({
      account: updatedUser,
    });
  };

  // swaps an organization that was merged away for the one it was merged into
  const replaceOrg = (removedOrgId: string, org: OrgMin) => {
    if (!user.account) {
      return;
    }

    const orgs = (user.account.orgs ?? []).filter(
      (o) => o.id !== removedOrgId && o.id !== org.id,
    );
    const updatedUser = {
      ...user.account,
      orgs: [...orgs, { id: org.id, name: org.name, slug: org.slug }],
    };

    storeOrg(org.id);
    window.localStorage.setItem("account", JSON.stringify(updatedUser));
    setUser({
      account: updatedUser,
    });
  };

  const authorize = async (code: string) => {
    setIsLoading(true);
    try {
      const resp = await fetch(`${VITE_API_URL}/auth/verify`, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          code,
        }),
      });
      if (!resp.ok) {
        console.log(resp.status);
        throw new Error("Invalid code");
      }

      const data = (await resp.json()) as VerifyRespType;

      if (data.error === "") {
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("account", JSON.stringify(data.account));

        setUser({
          account: data.account,
        });

        // the data isn't returned to the user so the location
        const account = data.account;
        if (!account.orgs || account.orgs.length == 0) {
          window.location.replace(`${VITE_BASE_URL}${onboardPath}`);
        } else {
          storeOrg(account.orgs[0].id);
          const slug = account.orgs[0].slug;
          const platform = data.is_shopify ? "?platform=shopify" : "";
          window.location.replace(
            `${VITE_BASE_URL}/${slug}${dashboardPath}${platform}`,
          );
        }
      } else {
        console.log(data.error);
        throw new Error(data.error);
      }
    } catch (e) {
      setIsLoading(false);
      console.error("Login failed:", e);
      throw e;
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await fetch(`${VITE_API_URL}/auth/logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      localStorage.removeItem("access_token");
      localStorage.removeItem("account");
      setUser({ account: undefined });
      removeOrg();
    } catch (e) {
      setIsLoading(false);
      console.error("Logout error:", e);
    }
  };

  const handleTokenInvalid = async () => {
    console.warn("Token invalid - logging out user");
    localStorage.removeItem("access_token");
    localStorage.removeItem("account");
    setUser({ account: undefined });
    removeOrg();
    // Redirect to login page
    window.location.href = `${VITE_BASE_URL}${loginPath}`;
  };

  const getToken = () => {
    const token = window.localStorage.getItem("access_token");

    if (!token) {
      return;
    }

    return token;
  };

  const setToken = (token: string) => {
    window.localStorage.setItem("access_token", token);
  };

  const getOrg = (orgId: string) => {
    return (
      user.account?.orgs.find((org) => org.id === orgId) ??
      user.account?.orgs[0]
    );
  };

  return (
    <AuthContext
      value={{
        user,
        handleUser,
        replaceOrg,
        logout,
        isLoading,
        authorize,
        getToken,
        setToken,
        getOrg,
        handleTokenInvalid,
      }}
    >
      {children}
    </AuthContext>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

export { AuthProvider, useAuth };
